import React, { useState } from 'react';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CButton,
  CAlert,
  CSpinner,
  CInputGroup,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilUserPlus, 
  cilEnvelopeClosed, 
  cilLockLocked, 
  cilLowVision, 
  cilViewColumn 
} from '@coreui/icons';
import { authAPI } from '../../services/api';
import { Link, useNavigate } from 'react-router-dom';

const Register = ({ onRegisterSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.register(
        formData.email,
        formData.password
      );
      
      if (response.data.success) {
        setSuccess('Registration successful! Redirecting to login...');
        
        // Clear form
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
        });
        
        // Redirect to login after 2 seconds
        setTimeout(() => {
          if (onRegisterSuccess) {
            onRegisterSuccess();
          } else {
            navigate('/login');
          }
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container d-flex align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={6} lg={5} xl={4}>
            <CCard className="p-4 shadow">
              <CCardHeader className="text-center bg-transparent border-0 pt-4">
                <CIcon icon={cilUserPlus} size="3xl" className="text-primary mb-3" />
                <h3>Create Account</h3>
                <p className="text-medium-emphasis">Join our Partner Portal</p>
              </CCardHeader>
              <CCardBody className="p-4">
                {error && (
                  <CAlert color="danger" className="mb-3">
                    {error}
                  </CAlert>
                )}
                
                {success && (
                  <CAlert color="success" className="mb-3">
                    {success}
                  </CAlert>
                )}
                
                <CForm onSubmit={handleSubmit}>
                  {/* Email */}
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-semibold">
                      Email Address
                    </label>
                    <CFormInput
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Password with Eye Icon */}
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label fw-semibold">
                      Password
                    </label>
                    <CInputGroup>
                      <CFormInput
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                      <CButton
                        type="button"
                        color="secondary"
                        variant="outline"
                        onClick={togglePasswordVisibility}
                        className="password-toggle-btn"
                      >
                        <CIcon icon={showPassword ? cilLowVision : cilViewColumn} />
                      </CButton>
                    </CInputGroup>
                    <div className="form-text">Must be at least 6 characters</div>
                  </div>

                  {/* Confirm Password with Eye Icon */}
                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label fw-semibold">
                      Confirm Password
                    </label>
                    <CInputGroup>
                      <CFormInput
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                      />
                      <CButton
                        type="button"
                        color="secondary"
                        variant="outline"
                        onClick={toggleConfirmPasswordVisibility}
                        className="password-toggle-btn"
                      >
                        <CIcon icon={showConfirmPassword ? cilLowVision : cilViewColumn} />
                      </CButton>
                    </CInputGroup>
                  </div>

                  <CButton 
                    type="submit" 
                    color="primary" 
                    className="w-100 py-2 mb-3 fw-semibold"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <CSpinner size="sm" className="me-2" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </CButton>
                </CForm>

                <div className="text-center">
                  <p className="text-medium-emphasis mb-0">
                    Already have an account?{' '}
                    <Link to="/login" className="text-decoration-none fw-semibold">
                      Sign In
                    </Link>
                  </p>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default Register;